import { useEffect, useRef, useState } from 'react';
import type RFB from '@novnc/novnc';
import type { RfbDisconnectEvent } from '@novnc/novnc';
import { ClipboardCopy, Maximize2, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useScreenClipboard } from '../../hooks/useScreenClipboard';
import { canEnterFullscreen, enterFullscreen } from '../../lib/fullscreen';
import { disconnectMessage, type OpenScreen } from '../../lib/mission/workshopScreen';

type ViewerState = 'connecting' | 'connected';

const ICON =
  'inline-flex h-7 w-7 items-center justify-center rounded-lg text-desk-soft transition hover:bg-desk-subtle hover:text-desk-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus';

/**
 * The live screen of the forms Chrome (PLAN-ATELIER-OTTO § 16.6): noVNC's client, loaded only when
 * the screen is opened, connected once through the gate's one-time path. Read-only unless the
 * database granted the right to act — and then x11vnc itself holds the viewer to it. While acting,
 * copy and paste pass between the computer and the machine (`useScreenClipboard`).
 */
export function WorkshopScreenViewer({ screen, onClose }: { screen: OpenScreen; onClose: (reason: string | null) => void }) {
  const frame = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ViewerState>('connecting');
  const [client, setClient] = useState<RFB | null>(null);
  const clipboard = useScreenClipboard(frame, client, !screen.viewOnly && state === 'connected');
  const fullscreen = canEnterFullscreen();

  useEffect(() => {
    let rfb: RFB | null = null;
    let disposed = false;
    void import('@novnc/novnc').then(({ default: Rfb }) => {
      if (disposed || !canvas.current) return;
      rfb = new Rfb(canvas.current, screen.websocketUrl, { credentials: { password: screen.password }, shared: true });
      rfb.viewOnly = screen.viewOnly;
      rfb.scaleViewport = true;
      rfb.resizeSession = false;
      rfb.background = 'transparent';
      const connected = rfb;
      rfb.addEventListener('connect', () => {
        setState('connected');
        setClient(connected);
      });
      rfb.addEventListener('disconnect', (event) => {
        if (!disposed) onClose(disconnectMessage((event as RfbDisconnectEvent).detail.clean));
      });
      rfb.addEventListener('securityfailure', () => {
        if (!disposed) onClose("La machine a refusé le mot de passe de l'écran. Rouvre-le.");
      });
    }, () => {
      if (!disposed) onClose("Le lecteur d'écran n'a pas pu être chargé. Recharge la page.");
    });
    return () => {
      disposed = true;
      setClient(null);
      rfb?.disconnect();
    };
  }, [screen, onClose]);

  return (
    <div
      ref={frame}
      className="overflow-hidden rounded-xl bg-desk-card ring-1 ring-desk-line [&:fullscreen]:flex [&:fullscreen]:flex-col [&:fullscreen]:rounded-none [&:fullscreen]:bg-neutral-950"
    >
      <div className="flex items-center gap-2 border-b border-desk-line bg-desk-card px-3 py-1.5 text-xs text-desk-soft">
        <span className={twMerge('h-2 w-2 rounded-full', state === 'connected' ? 'bg-desk-accent' : 'animate-pulse bg-desk-signal')} aria-hidden="true" />
        <span role="status" className="font-medium">
          {state === 'connected' ? (screen.viewOnly ? 'En direct, lecture seule' : 'En direct, tu peux agir') : 'Connexion à la machine…'}
        </span>
        {state === 'connected' && !screen.viewOnly && (
          <span className="hidden text-desk-muted sm:inline">· copier-coller : {clipboard.keys}</span>
        )}
        {fullscreen && (
          <button type="button" onClick={() => enterFullscreen(frame.current)} className={twMerge(ICON, 'ml-auto')} aria-label="Plein écran" title="Plein écran">
            <Maximize2 size={14} aria-hidden="true" />
          </button>
        )}
        <button type="button" onClick={() => onClose(null)} className={twMerge(ICON, !fullscreen && 'ml-auto')} aria-label="Fermer l'écran" title="Fermer l'écran">
          <X size={15} aria-hidden="true" />
        </button>
      </div>
      {clipboard.copyFallback !== null && (
        <div role="status" className="flex flex-wrap items-center gap-2 border-b border-desk-line bg-desk-subtle px-3 py-1.5 text-xs text-desk-ink">
          Copié dans la machine ; ton navigateur demande un clic pour le mettre dans ton presse-papier.
          <button type="button" onClick={clipboard.copyNow} className="inline-flex items-center gap-1 font-medium underline underline-offset-2">
            <ClipboardCopy size={13} aria-hidden="true" /> Copier
          </button>
          <button type="button" onClick={clipboard.dismissCopy} className="ml-auto text-desk-soft hover:text-desk-ink" aria-label="Ignorer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}
      <div ref={canvas} className="aspect-[16/10] w-full bg-neutral-900 [:fullscreen>&]:aspect-auto [:fullscreen>&]:flex-1" />
    </div>
  );
}
